/**
 * System Defaults — Valores por defecto del SISTEMA (no de un juego específico)
 *
 * DEFAULT_MONEDA, IDIOMA_DEFAULT, etc. vivían en games/lotowheel/defaults.js
 * pero no son del juego — son del sistema. Este archivo es su SSOT.
 *
 * Consumido por:
 * - backend: contabilidad, auth, navegación, pozos, seamless
 * - shared/config/games/lotowheel/defaults.js (re-importa para backward compat)
 */

const DEFAULT_MONEDA = {
    codigo: 'PEN',
    simbolo: 'S/',
    decimales: 2,
    separadorDecimal: '.',
    separadorMiles: ',',
};

const IDIOMA_DEFAULT = 'es';

// Zona horaria por defecto del sistema (IANA). El SSOT real por brand vive en
// configuracion_sistema.zona_horaria_default (DB de cada tenant); esto es el
// fallback cuando la fila no está sembrada (bases viejas como Perú).
const DEFAULT_ZONA_HORARIA = 'America/Lima';

// Offset UTC fijo por zona IANA, en formato modificador de SQLite ('±HH:MM').
// Se usa para cortar el "día contable" en hora local del brand en queries
// (DATE(created_at, OFFSET)) — el extrato bancario y los operadores razonan en
// hora local, no UTC. Mapa explícito (sin dependencia de librería tz) porque
// los mercados donde operamos NO tienen horario de verano:
//   - Brasil abolió el DST en 2019 → America/Sao_Paulo = UTC−3 estable.
//   - Perú nunca tuvo DST → America/Lima = UTC−5 estable.
// Si en el futuro entra un mercado CON DST, este mapa debe migrar a Intl/tz real.
const OFFSET_SQLITE_POR_ZONA = {
    'America/Sao_Paulo': '-03:00',
    'America/Lima': '-05:00',
};

/**
 * Devuelve el modificador de offset de SQLite para una zona IANA.
 * @param {string} zonaHoraria - Zona IANA (ej. 'America/Sao_Paulo').
 * @returns {string} Modificador SQLite ('-03:00'). Default a Perú si la zona
 *   no está mapeada (fail-safe: no rompe la query, solo desfasa el corte).
 */
function offsetSqliteDeZonaHoraria(zonaHoraria) {
    return OFFSET_SQLITE_POR_ZONA[zonaHoraria] || OFFSET_SQLITE_POR_ZONA[DEFAULT_ZONA_HORARIA];
}

/**
 * Resuelve el modificador SQLite del día local del brand leyendo configuracion_sistema.
 * SSOT para las queries admin que buckean por día/hora — sin él, DATE()/strftime() sobre
 * created_at UTC desfasan el borde de día (Brasil −3, Perú −5). Fail-safe a la zona default.
 * @param {object} db - conexión con queryOne(sql, params).
 * @returns {string} modificador SQLite ('-03:00').
 */
function resolverOffsetDiaLocalDelBrand(db) {
    let zona = DEFAULT_ZONA_HORARIA;
    try {
        const row = db.queryOne('SELECT valor FROM configuracion_sistema WHERE clave = ?', ['zona_horaria_default']);
        if (row && row.valor) zona = row.valor;
    } catch (_) {
        /* fail-safe: zona default */
    }
    return offsetSqliteDeZonaHoraria(zona);
}

// Conversiones de tiempo — uso transversal en scheduler, timers, countdowns.
// SSOT para evitar `* 1000` / `/ 1000` mágicos esparcidos por backend y fronts.
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MS_PER_MINUTE = MS_PER_SECOND * SECONDS_PER_MINUTE;

// Pozo acumulado — config base reutilizable por cualquier juego (lotowheel, bingo, slots, etc.)
const DEFAULT_POZO = {
    min_mult: 1.0,
    max_mult: 3.0,
    base: 100000, // Pozo base en centavos (1000 soles)
    porcentaje_aporte: 0.5, // % de cada apuesta que va al pozo
};

// Compatibilidad: ESM (fronts) + CJS (backend)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        DEFAULT_MONEDA,
        IDIOMA_DEFAULT,
        DEFAULT_ZONA_HORARIA,
        offsetSqliteDeZonaHoraria,
        resolverOffsetDiaLocalDelBrand,
        DEFAULT_POZO,
        MS_PER_SECOND,
        SECONDS_PER_MINUTE,
        MS_PER_MINUTE,
    };
}

export {
    DEFAULT_MONEDA,
    IDIOMA_DEFAULT,
    DEFAULT_ZONA_HORARIA,
    offsetSqliteDeZonaHoraria,
    resolverOffsetDiaLocalDelBrand,
    DEFAULT_POZO,
    MS_PER_SECOND,
    SECONDS_PER_MINUTE,
    MS_PER_MINUTE,
};
